import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
    LayoutDashboard, Users, AlertTriangle, ShieldCheck, Search, LogOut, TrendingUp,
    ChevronRight, Heart, Activity, Scale, Droplet, ArrowRight, Sparkles,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { KioskVitalsReport } from "../components/KioskVitalsReport";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";

const FLAG_STYLE = {
    urgent_leave: { bg: "bg-red-100", text: "text-red-700", label: "Urgent Leave" },
    take_a_break: { bg: "bg-amber-100", text: "text-amber-700", label: "Take a Break" },
    ok: { bg: "bg-emerald-100", text: "text-emerald-700", label: "Optimal" },
    no_data: { bg: "bg-gray-100", text: "text-gray-600", label: "No Scans" },
};

export default function EmployerDashboard() {
    const { user, logout } = useAuth();
    const { search } = useLocation();
    const navigate = useNavigate();
    const params = new URLSearchParams(search);
    const employeeId = params.get("employee");

    const [stats, setStats] = useState(null);
    const [employees, setEmployees] = useState([]);
    const [q, setQ] = useState("");
    const [filter, setFilter] = useState("all"); // all|urgent|break|ok
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const [{ data: s }, { data: e }] = await Promise.all([
                api.get("/employer/stats"),
                api.get("/employer/employees"),
            ]);
            setStats(s);
            setEmployees(e);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not load employer dashboard");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => {
        return employees
            .filter((e) => {
                if (filter === "urgent") return e.hr_flag?.code === "urgent_leave";
                if (filter === "break") return e.hr_flag?.code === "take_a_break";
                if (filter === "ok") return e.hr_flag?.code === "ok";
                return true;
            })
            .filter((e) => !q || (e.name || "").toLowerCase().includes(q.toLowerCase()) || (e.email || "").toLowerCase().includes(q.toLowerCase()));
    }, [employees, q, filter]);

    const selected = useMemo(() => employees.find((e) => e.id === employeeId), [employees, employeeId]);

    const pieData = stats ? [
        { name: "Optimal", value: stats.optimal, color: "#10b981" },
        { name: "Take a Break", value: stats.take_a_break, color: "#f59e0b" },
        { name: "Urgent Leave", value: stats.urgent_leave, color: "#ef4444" },
        { name: "No Scans", value: stats.no_data, color: "#9ca3af" },
    ].filter((d) => d.value > 0) : [];

    const scoreDist = useMemo(() => {
        const bins = { "40–59": 0, "60–74": 0, "75–89": 0, "90–100": 0 };
        for (const e of employees) {
            const s = e.health_score || 0;
            if (s >= 90) bins["90–100"] += 1;
            else if (s >= 75) bins["75–89"] += 1;
            else if (s >= 60) bins["60–74"] += 1;
            else bins["40–59"] += 1;
        }
        return Object.entries(bins).map(([name, count]) => ({ name, count }));
    }, [employees]);

    if (loading) return <div className="min-h-screen grid place-items-center text-gray-500">Loading employer dashboard…</div>;

    // Detail view
    if (selected) {
        return (
            <div className="min-h-screen bg-white" data-testid="employer-employee-detail">
                <TopBar user={user} logout={logout} />
                <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
                    <button
                        onClick={() => navigate("/employer")}
                        className="text-sm text-gray-500 hover:text-[var(--medx-red)] inline-flex items-center gap-1 mb-6"
                        data-testid="employer-back"
                    >
                        <ChevronRight className="w-4 h-4 rotate-180" /> All employees
                    </button>
                    <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
                        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[var(--medx-red)] to-[var(--medx-orange)] grid place-items-center text-white text-2xl font-display font-bold shrink-0">
                            {(selected.name || "").split(" ").map((s) => s[0]).slice(0, 2).join("")}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-xs font-mono tracking-[0.22em] text-[var(--medx-red)]">EMPLOYEE FILE</div>
                            <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">{selected.name}</h1>
                            <div className="text-sm text-gray-500">{selected.email} · Age {selected.age} · {selected.gender}</div>
                        </div>
                        <div className="text-right">
                            <div className="text-xs font-mono text-gray-500">HEALTH SCORE</div>
                            <div className="font-display text-4xl font-bold brand-gradient-text">{selected.health_score ?? "—"}</div>
                        </div>
                    </div>
                    {selected.latest_vitals ? (
                        <KioskVitalsReport vitals={selected.latest_vitals} patient={selected} />
                    ) : (
                        <div className="card-soft p-10 text-center text-sm text-gray-500">This employee has no kiosk scans yet.</div>
                    )}
                </main>
            </div>
        );
    }

    // Main dashboard
    return (
        <div className="min-h-screen bg-[var(--medx-cream)]" data-testid="employer-dashboard">
            <TopBar user={user} logout={logout} />

            <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
                <div className="mb-8">
                    <div className="text-xs font-mono tracking-[0.22em] text-[var(--medx-red)]">EMPLOYER DASHBOARD</div>
                    <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Workforce wellness at {user?.company_name || "your company"}</h1>
                    <p className="text-gray-500 mt-1">Live triage view across every employee scan taken at the Medx AI Kiosk.</p>
                </div>

                {/* KPI cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <KpiCard
                        icon={Users}
                        label="Total employees"
                        value={stats?.total_employees ?? 0}
                        tone="neutral"
                        testId="kpi-total"
                    />
                    <KpiCard
                        icon={TrendingUp}
                        label="Avg health score"
                        value={stats?.avg_health_score ?? 0}
                        suffix="/100"
                        tone="positive"
                        testId="kpi-avg-score"
                    />
                    <KpiCard
                        icon={AlertTriangle}
                        label="Take a Break"
                        value={stats?.take_a_break ?? 0}
                        tone="warning"
                        testId="kpi-take-a-break"
                        onClick={() => setFilter("break")}
                    />
                    <KpiCard
                        icon={AlertTriangle}
                        label="Urgent Leave"
                        value={stats?.urgent_leave ?? 0}
                        tone="critical"
                        testId="kpi-urgent"
                        onClick={() => setFilter("urgent")}
                    />
                </div>

                {/* Charts */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
                    <div className="card-soft p-5 lg:col-span-1" data-testid="chart-triage-pie">
                        <div className="text-xs font-mono tracking-wider text-gray-500 mb-3">TRIAGE BREAKDOWN</div>
                        <div className="h-56">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={2}>
                                        {pieData.map((d, i) => <Cell key={i} fill={d.color} />)}
                                    </Pie>
                                    <Tooltip />
                                    <Legend verticalAlign="bottom" height={30} iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                    <div className="card-soft p-5 lg:col-span-2" data-testid="chart-score-dist">
                        <div className="text-xs font-mono tracking-wider text-gray-500 mb-3">HEALTH SCORE DISTRIBUTION</div>
                        <div className="h-56">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={scoreDist}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                                    <XAxis dataKey="name" stroke="#9ca3af" fontSize={11} />
                                    <YAxis stroke="#9ca3af" fontSize={11} />
                                    <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #f3f4f6" }} />
                                    <Bar dataKey="count" fill="#e63946" radius={[8, 8, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
                    <div className="relative flex-1 max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search employee name or email…"
                            className="w-full pl-9 pr-3 py-2.5 rounded-full border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm bg-white"
                            data-testid="employer-search"
                        />
                    </div>
                    <div className="flex items-center gap-1 flex-wrap" data-testid="employer-filters">
                        {[
                            { k: "all", label: "All" },
                            { k: "urgent", label: `Urgent Leave (${stats?.urgent_leave ?? 0})` },
                            { k: "break", label: `Take a Break (${stats?.take_a_break ?? 0})` },
                            { k: "ok", label: `Optimal (${stats?.optimal ?? 0})` },
                        ].map((f) => (
                            <button
                                key={f.k}
                                onClick={() => setFilter(f.k)}
                                className={`text-xs px-3 py-1.5 rounded-full transition-colors ${filter === f.k ? "bg-[var(--medx-red)] text-white" : "bg-white border border-gray-200 text-gray-700 hover:border-[var(--medx-orange)]"}`}
                                data-testid={`employer-filter-${f.k}`}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Table */}
                <div className="card-soft overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-gray-500 text-xs font-mono tracking-wider">
                            <tr>
                                <th className="text-left p-4">EMPLOYEE</th>
                                <th className="text-left p-4">AGE / SEX</th>
                                <th className="text-left p-4">SCORE</th>
                                <th className="text-left p-4">BP</th>
                                <th className="text-left p-4">HR</th>
                                <th className="text-left p-4">FLAG</th>
                                <th className="text-left p-4">LAST SCAN</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((e) => {
                                const flag = e.hr_flag || {};
                                const style = FLAG_STYLE[flag.code] || FLAG_STYLE.no_data;
                                const v = e.latest_vitals || {};
                                return (
                                    <tr key={e.id} className="border-t border-gray-100 hover:bg-orange-50/40 cursor-pointer" onClick={() => navigate(`/employer?employee=${e.id}`)} data-testid={`employer-row-${e.id}`}>
                                        <td className="p-4">
                                            <div className="font-medium">{e.name}</div>
                                            <div className="text-xs text-gray-400 truncate max-w-[240px]">{e.email}</div>
                                        </td>
                                        <td className="p-4 text-gray-700">{e.age} · {e.gender}</td>
                                        <td className="p-4 font-mono font-bold text-[var(--medx-red)]">{e.health_score ?? "—"}</td>
                                        <td className="p-4 text-gray-700">{v.blood_pressure_systolic || "—"}/{v.blood_pressure_diastolic || "—"}</td>
                                        <td className="p-4 text-gray-700">{v.heart_rate || "—"}</td>
                                        <td className="p-4">
                                            <span className={`text-[10px] font-mono px-2 py-1 rounded-full ${style.bg} ${style.text}`}>{style.label}</span>
                                        </td>
                                        <td className="p-4 text-xs text-gray-500">{e.last_session_at ? new Date(e.last_session_at).toLocaleDateString() : "—"}</td>
                                        <td className="p-4 text-right"><ChevronRight className="w-4 h-4 text-gray-300 inline" /></td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    {filtered.length === 0 && (
                        <div className="text-center text-sm text-gray-500 py-12">No employees match this filter.</div>
                    )}
                </div>

                <div className="mt-8 rounded-3xl p-5 bg-white border border-orange-100 flex items-center gap-3 text-sm" data-testid="employer-privacy-note">
                    <ShieldCheck className="w-5 h-5 text-[var(--medx-orange)]" />
                    Employees consent to share aggregated wellness data with their employer via the Medx AI Kiosk terms. Detailed medical records are only visible to the treating doctor.
                </div>
            </main>
        </div>
    );
}

function KpiCard({ icon: Icon, label, value, suffix, tone, testId, onClick }) {
    const tones = {
        neutral: "text-gray-800",
        positive: "text-emerald-600",
        warning: "text-amber-600",
        critical: "text-red-600",
    };
    return (
        <div
            onClick={onClick}
            className={`card-soft p-5 ${onClick ? "cursor-pointer hover:-translate-y-0.5 transition-transform" : ""}`}
            data-testid={testId}
        >
            <div className="flex items-center gap-2 text-xs font-mono tracking-wider text-gray-500">
                <Icon className="w-3 h-3" /> {label.toUpperCase()}
            </div>
            <div className={`font-display text-4xl font-bold mt-2 ${tones[tone]}`}>{value}{suffix && <span className="text-lg text-gray-400 ml-1">{suffix}</span>}</div>
        </div>
    );
}

function TopBar({ user, logout }) {
    return (
        <header className="bg-white border-b border-gray-100 sticky top-0 z-30">
            <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-2">
                    <div className="font-display text-xl font-bold">medx<span className="text-[var(--medx-orange)]">.</span>ai</div>
                    <span className="text-[10px] font-mono tracking-[0.22em] text-gray-400 hidden sm:inline">EMPLOYER PORTAL</span>
                </Link>
                <div className="flex items-center gap-4">
                    <div className="hidden sm:block text-right">
                        <div className="text-sm font-semibold">{user?.company_name || user?.name}</div>
                        <div className="text-xs text-gray-500">{user?.email}</div>
                    </div>
                    <button onClick={() => { logout(); window.location.href = "/"; }} className="btn-outline !py-2 !px-4 text-sm inline-flex items-center gap-1" data-testid="employer-logout">
                        <LogOut className="w-3 h-3" /> Sign out
                    </button>
                </div>
            </div>
        </header>
    );
}
